import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import DatePickerField from "../../components/common/DatePickerField";
import {
  fetchAuditLogs,
  fetchIndianStates,
  fetchStatutoryDashboard,
  fetchStatutoryModule,
  saveBonus,
  saveDraft,
  saveEpf,
  saveEsi,
  saveLwf,
  savePt,
  saveRegistrations,
  formatStatutoryError,
} from "./statutoryApi";
import {
  calcBonusLocal,
  calcEsiLocal,
  calcPfLocal,
  DEFAULT_PF_WAGE_CEILING,
  fmtINR,
  todayISO,
} from "./statutoryCalculators";
import {
  STATUTORY_SETUP_STEPS,
  STATUTORY_STEP_KEY,
  validateStatutoryFormat,
  formatHint,
  formatExample,
  DEFAULT_PF_STATE,
  PF_STATE_OPTIONS,
  detectPfState,
  getPfStateConfig,
  validatePfRegistration,
  pfLiveStatus,
} from "./statutoryFormats";
import PfRegistrationInput from "./PfRegistrationInput";
import SuccessCelebration from "../../components/ui/SuccessCelebration";
import ProfessionalTaxPanel from "./ProfessionalTaxPanel";
import { Field, focusFirstError, Toggle } from "./statutoryFormControls";
import { useToast } from "../Context/ToastContext";
import { useSetupProgress } from "../Context/SetupProgressContext";
import { markStatutorySetupConfigured } from "../../services/markStatutorySetupConfigured";
import { navigateToSettingsHome } from "../../components/settings/settingsExit";
import API from "../../services/api";
import { Shield } from "lucide-react";
import PfAdvancedSettings from "./PfAdvancedSettings";
import "./statutory.css";

const STATUTORY_FINISH_KEY = "brixigo.statutorySetup.finished";

function parseLocalDate(iso) {
  if (!iso) return null;
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatLongDate(iso) {
  const d = parseLocalDate(iso);
  if (!d) return "—";
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
}

function nextMonthFirstISO(iso) {
  const d = parseLocalDate(iso) || new Date();
  const next = new Date(d.getFullYear(), d.getMonth() + 1, 1);
  const y = next.getFullYear();
  const m = String(next.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
}

/** First day of the current month (YYYY-MM-DD, local time). */
function currentMonthFirstISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

/**
 * Effective From default: the 1st of the current month. A saved date that is already in the
 * current month or later (e.g. a change scheduled for next month) is kept.
 */
function defaultEffectiveFrom(saved) {
  const first = currentMonthFirstISO();
  const iso = saved ? String(saved).slice(0, 10) : "";
  return iso && iso >= first ? iso : first;
}

function isFirstOfMonth(iso) {
  const d = parseLocalDate(iso);
  return Boolean(d && d.getDate() === 1);
}

const TABS = [
  { id: "epf", label: "EPF" },
  { id: "esi", label: "ESI" },
  { id: "pt", label: "Professional Tax" },
  { id: "lwf", label: "Labour Welfare Fund" },
  { id: "bonus", label: "Statutory Bonus" },
  { id: "dashboard", label: "Compliance Dashboard" },
  { id: "registrations", label: "Government Registrations" },
  { id: "audit", label: "Audit Logs" },
];

/** Map header-card labels (API + fallback) → setup tab ids. Header cards are commented out below. */
// eslint-disable-next-line no-unused-vars
const HEADER_CARD_TAB = {
  EPF: "epf",
  ESI: "esi",
  PT: "pt",
  "Professional Tax": "pt",
  LWF: "lwf",
  "Labour Welfare Fund": "lwf",
  Bonus: "bonus",
  "Statutory Bonus": "bonus",
};

function LiveCalcHeader({ title }) {
  return (
    <div className="stat-calc-head">
      <h3>{title}</h3>
      <span className="stat-live-badge">
        <span className="stat-live-dot" />
        Live
      </span>
    </div>
  );
}

function CalcRow({ label, value, total = false }) {
  const [flash, setFlash] = useState(false);
  const prev = useRef(value);

  useEffect(() => {
    if (prev.current !== value) {
      prev.current = value;
      setFlash(true);
      const t = setTimeout(() => setFlash(false), 450);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [value]);

  return (
    <div className={`stat-calc-row${total ? " stat-calc-row--total" : ""}`}>
      <span>{label}</span>
      <span className={`stat-calc-value${flash ? " flash" : ""}`}>{value}</span>
    </div>
  );
}

export default function StatutoryComponents({ forcedTab } = {}) {
  const toast = useToast();
  const { refetch: refetchSetupProgress } = useSetupProgress() || {};
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get("tab");
  const setupIds = STATUTORY_SETUP_STEPS.map((s) => s.id);
  const initialTab = (() => {
    // Prefer explicit URL / route over session so card/sidebar navigation lands correctly.
    if (tabFromUrl && setupIds.includes(tabFromUrl)) return tabFromUrl;
    if (forcedTab && setupIds.includes(forcedTab)) return forcedTab;
    try {
      const saved = sessionStorage.getItem(STATUTORY_STEP_KEY);
      if (saved && setupIds.includes(saved)) return saved;
    } catch {
      /* ignore */
    }
    return "epf";
  })();
  const [activeTab, setActiveTab] = useState(initialTab);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [, setDraftSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dashboard, setDashboard] = useState(null);
  const [states, setStates] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});
  const [completedSteps, setCompletedSteps] = useState(() => new Set());
  const [finished, setFinished] = useState(false);
  const [epfEffectiveModal, setEpfEffectiveModal] = useState(null);
  const draftTimer = useRef(null);

  const stepIndex = Math.max(0, setupIds.indexOf(activeTab));
  const isSetupStep = setupIds.includes(activeTab);
  const hasNextSetupStep =
    isSetupStep && stepIndex >= 0 && stepIndex < STATUTORY_SETUP_STEPS.length - 1;

  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab && TABS.some((t) => t.id === tab) && tab !== activeTab) {
      setActiveTab(tab);
    }
  }, [searchParams, activeTab]);

  const selectTab = useCallback(
    (tabId, opts = {}) => {
      // Guided flow: only allow current step, completed steps, or the next unlocked step.
      const idx = setupIds.indexOf(tabId);
      if (idx >= 0 && !opts.force) {
        const allowed =
          completedSteps.has(tabId)
          || tabId === activeTab
          || idx === 0
          || completedSteps.has(setupIds[idx - 1]);
        if (!allowed) {
          toast.error("Complete the current step before moving ahead.");
          return;
        }
      }
      setFieldErrors({});
      setError("");
      setActiveTab(tabId);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tabId);
        return next;
      });
      try {
        sessionStorage.setItem(STATUTORY_STEP_KEY, tabId);
      } catch {
        /* ignore */
      }
    },
    [setSearchParams, completedSteps, activeTab, setupIds, toast]
  );

  const clearFieldError = (key) => {
    setFieldErrors((e) => (e[key] ? { ...e, [key]: undefined } : e));
  };

  const [reg, setReg] = useState({
    company_pan: "",
    company_tan: "",
    epfo_establishment_code: "",
    esic_registration_number: "",
    pt_registration_number: "",
    pt_enrollment_number: "",
    lwf_number: "",
    gst_number: "",
    cin_number: "",
    shop_establishment_number: "",
    attachments: [],
  });

  const [epf, setEpf] = useState({
    is_enabled: false,
    establishment_code: "",
    employer_contribution_mode: "capped_15000",
    effective_from: currentMonthFirstISO(),
    pf_applies_to: ["all"],
    include_employer_pf_in_ctc: true,
    include_employer_pf_charges_in_ctc: false,
    options: {
      include_employer_in_ctc: false,
      include_edli_in_ctc: false,
      include_admin_in_ctc: false,
      allow_employee_override: true,
      allow_vpf: true,
      prorate_pf: true,
      consider_other_earnings_below_cap: false,
      auto_include_new_employees: true,
    },
  });

  const PF_EMPLOYMENT_TYPES = [
    { key: "all", label: "All Employees" },
    { key: "regular", label: "Regular Employee" },
    { key: "contract", label: "Contract Employee" },
    { key: "intern", label: "Intern" },
    { key: "apprentice", label: "Apprentice" },
    { key: "other", label: "Other" },
  ];

  const ESI_EMPLOYMENT_TYPES = [
    { key: "all", label: "All Employees" },
    { key: "regular", label: "Regular Employee" },
    { key: "contract", label: "Contract Employee" },
    { key: "intern", label: "Intern" },
    { key: "apprentice", label: "Apprentice" },
    { key: "other", label: "Other" },
  ];

  const [esi, setEsi] = useState({
    is_enabled: false,
    registration_number: "",
    coverage_state: "TG",
    effective_from: currentMonthFirstISO(),
    esi_applies_to: ["all"],
    employee_contribution_rate: "0.75",
    employer_contribution_rate: "3.25",
    esi_wage_basis: "gross",
    include_employer_esi_in_ctc: true,
    include_employer_esi_charges_in_ctc: false,
    options: {
      auto_enroll: true,
      continue_till_period_end: true,
      employee_override: true,
      auto_remove_after_limit: true,
    },
  });

  const [pt, setPt] = useState({
    is_enabled: false,
    registration_number: "",
    enrollment_number: "",
    state_code: "TG", // Telangana is the default PT state (same as ProfessionalTaxPanel)
    deduction_cycle: "monthly",
    effective_from: todayISO(),
  });

  // Last saved PT configuration (null until one exists) — PT tab is ProfessionalTaxPanel.
  const [ptSaved, setPtSaved] = useState(null);
  const [lwf, setLwf] = useState({
    is_enabled: false,
    state_code: "TG", // Telangana is the default LWF state
    registration_number: "",
    employee_contribution: 20,
    employer_contribution: 40,
    frequency: "yearly",
    salary_threshold: null,
    effective_from: todayISO(),
    eligibility_rules: { gender_rules: "", worker_category: "" },
  });

  const [bonus, setBonus] = useState({
    is_enabled: false,
    payment_frequency: "yearly",
    bonus_percent: 8.33,
    salary_ceiling: 21000,
    min_worked_days: 30,
    effective_from: todayISO(),
  });

  const [pfSubTab, setPfSubTab] = useState("basic");
  // Not persisted separately — derived from the saved code's prefix (TS/…, AP/…).
  const [pfState, setPfState] = useState(DEFAULT_PF_STATE);

  const [pfAdvanced, setPfAdvanced] = useState({
    allow_employee_override: true,
    allow_vpf: true,
    vpf_max_percent: 100,
    prorate_pf_for_mid_month: true,
    consider_ot_in_pf_wage: false,
    include_bonus_in_pf_wage: false,
    round_pf_to_nearest: "rupee",
    restrict_employer_share_on_exit: false,
  });

  const [pfCalc, setPfCalc] = useState({ basicPay: 15000, da: 0 });
  // Statutory PF ceilings from the backend (GET /statutory/module → epf_rules).
  const [epfRules, setEpfRules] = useState(null);
  const pfWageCeiling = Number(epfRules?.wage_ceiling) || DEFAULT_PF_WAGE_CEILING;
  const pfCeilingLabel = `₹${pfWageCeiling.toLocaleString("en-IN")}`;
  const [esiCalc, setEsiCalc] = useState({ gross: 18000, basic: 10000 });
  const [lwfCalc, setLwfCalc] = useState({ salary: 15000 });
  const [bonusCalc, setBonusCalc] = useState({ basicDa: 18000, workedDays: 30 });

  const markDirty = useCallback(() => setDirty(true), []);

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [mod, dash, stList] = await Promise.all([
        fetchStatutoryModule(),
        fetchStatutoryDashboard(),
        fetchIndianStates(),
      ]);
      setDashboard(dash);
      setStates(stList || []);
      setEpfRules(mod.epf_rules || null);
      if (mod.registrations) {
        setReg((p) => ({ ...p, ...mod.registrations, attachments: mod.registrations.attachments || [] }));
      }
      if (mod.epf) {
        const opts = mod.epf.options || {};
        setEpf((p) => ({
          ...p,
          is_enabled: mod.epf.is_enabled,
          establishment_code: mod.epf.establishment_code || "",
          employer_contribution_mode: mod.epf.employer_contribution_mode || "capped_15000",
          effective_from: defaultEffectiveFrom(mod.epf.effective_from),
          pf_applies_to: opts.pf_applies_to || mod.epf.pf_applies_to || p.pf_applies_to || ["all"],
          include_employer_pf_in_ctc:
            opts.include_employer_in_ctc
            ?? mod.epf.include_employer_pf_in_ctc
            ?? p.include_employer_pf_in_ctc
            ?? false,
          include_employer_pf_charges_in_ctc:
            opts.include_pf_charges_in_ctc
            ?? opts.include_edli_in_ctc
            ?? opts.include_admin_in_ctc
            ?? mod.epf.include_employer_pf_charges_in_ctc
            ?? p.include_employer_pf_charges_in_ctc
            ?? false,
          options: { ...p.options, ...opts },
        }));
      }
      setPfState(
        detectPfState(mod.epf?.establishment_code || mod.registrations?.epfo_establishment_code)
          || DEFAULT_PF_STATE
      );
      if (mod.epf?.advanced) {
        setPfAdvanced((p) => ({ ...p, ...mod.epf.advanced }));
      }
      if (mod.esi) {
        setEsi((p) => ({
          ...p,
          ...mod.esi,
          effective_from: defaultEffectiveFrom(mod.esi.effective_from),
          coverage_state: mod.esi.coverage_state || p.coverage_state || "TG",
          esi_applies_to: mod.esi.esi_applies_to || p.esi_applies_to || ["all"],
          options: { ...p.options, ...(mod.esi.options || {}) },
        }));
      }
      if (mod.pt) {
        setPt((p) => ({ ...p, ...mod.pt }));
        setPtSaved(mod.pt);
      }
      if (mod.lwf) setLwf((p) => ({ ...p, ...mod.lwf }));
      if (mod.bonus) setBonus((p) => ({ ...p, ...mod.bonus }));
      setDirty(false);
    } catch (e) {
      setError(e?.detail || e?.message || "Failed to load statutory module");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  useEffect(() => {
    let cancelled = false;
    async function hydrateCompletionState() {
      try {
        const hrRes = await API.get("/organisation/me/hr-settings");
        const payroll = hrRes?.data?.hr_settings?.payroll || {};
        const done =
          payroll.statutory_completed === true || Boolean(payroll.statutory_saved_at);
        if (done && !cancelled) {
          setCompletedSteps(new Set(setupIds));
          try {
            sessionStorage.setItem(STATUTORY_FINISH_KEY, "1");
          } catch {
            /* ignore */
          }
          return;
        }
        if (sessionStorage.getItem(STATUTORY_FINISH_KEY) === "1") {
          await markStatutorySetupConfigured();
          if (!cancelled) {
            setCompletedSteps(new Set(setupIds));
            await refetchSetupProgress?.();
          }
        }
      } catch {
        /* non-blocking */
      }
    }
    hydrateCompletionState();
    return () => {
      cancelled = true;
    };
  }, [refetchSetupProgress, setupIds]);

  useEffect(() => {
    if (!dirty) return undefined;
    if (draftTimer.current) clearTimeout(draftTimer.current);
    draftTimer.current = setTimeout(() => {
      setDraftSaving(true);
      saveDraft({ reg, epf: { ...epf, advanced: pfAdvanced }, esi, pt, lwf, bonus })
        .catch(() => {})
        .finally(() => setDraftSaving(false));
    }, 1200);
    return () => clearTimeout(draftTimer.current);
  }, [dirty, reg, epf, pfAdvanced, esi, pt, lwf, bonus]);

  useEffect(() => {
    if (activeTab !== "audit") return;
    fetchAuditLogs().then(setAuditLogs).catch(() => setAuditLogs([]));
  }, [activeTab]);

  const pfResult = useMemo(
    () =>
      calcPfLocal({
        basicPay: pfCalc.basicPay,
        da: pfCalc.da,
        isCapped: epf.employer_contribution_mode !== "actual_wage",
        wageCap: pfWageCeiling,
        epsCap: Number(epfRules?.eps_wage_ceiling) || undefined,
        edliCap: Number(epfRules?.edli_wage_ceiling) || undefined,
      }),
    [pfCalc, epf.employer_contribution_mode, pfWageCeiling, epfRules]
  );

  const esiWageValue = esi.esi_wage_basis === "basic" ? esiCalc.basic : esiCalc.gross;
  const esiResult = useMemo(() => calcEsiLocal(esiWageValue), [esiWageValue]);
  const bonusResult = useMemo(
    () => calcBonusLocal({ basicDa: bonusCalc.basicDa, bonusPercent: bonus.bonus_percent, workedDays: bonusCalc.workedDays }),
    [bonusCalc, bonus.bonus_percent]
  );

  const validateCurrentStep = useCallback(() => {
    const errors = {};

    const checkFmt = (key, value, required = false) => {
      const res = validateStatutoryFormat(key, value, { required });
      if (!res.ok) errors[key] = res.error;
      return res;
    };

    if (activeTab === "registrations") {
      [
        "company_pan",
        "company_tan",
        "epfo_establishment_code",
        "esic_registration_number",
        "pt_registration_number",
        "pt_enrollment_number",
        "gst_number",
        "cin_number",
      ].forEach((key) => checkFmt(key, reg[key], false));
    }

    if (activeTab === "epf" && epf.is_enabled) {
      const code = epf.establishment_code || reg.epfo_establishment_code;
      const pfRes = validatePfRegistration(code, pfState, { required: true });
      if (!pfRes.ok) errors.epfo_establishment_code = pfRes.error;
      if (!epf.employer_contribution_mode) {
        errors.employer_contribution_mode = "Choose an employer contribution mode.";
      }
      if (!epf.effective_from) {
        errors.effective_from = "Effective from date is required when EPF is enabled.";
      }
    }

    if (activeTab === "esi" && esi.is_enabled) {
      const code = esi.registration_number || reg.esic_registration_number;
      checkFmt("esic_registration_number", code, true);
      if (!esi.coverage_state) {
        errors.coverage_state = "Coverage state is required when ESI is enabled.";
      }
      if (!esi.esi_wage_basis) {
        errors.esi_wage_basis = "ESI Wage Basis is required. It determines whether ESI is calculated on Gross Salary or Basic Salary.";
      }
    }

    if (activeTab === "pt" && pt.is_enabled) {
      checkFmt("pt_registration_number", pt.registration_number || reg.pt_registration_number, true);
      if (!pt.state_code) {
        errors.pt_state_code = "State is required when Professional Tax is enabled.";
      }
    }

    if (activeTab === "lwf" && lwf.is_enabled) {
      if (!lwf.state_code) errors.lwf_state_code = "State is required when LWF is enabled.";
      if (lwf.employee_contribution === "" || Number(lwf.employee_contribution) < 0) {
        errors.lwf_employee = "Enter a valid employee contribution amount.";
      }
      if (lwf.employer_contribution === "" || Number(lwf.employer_contribution) < 0) {
        errors.lwf_employer = "Enter a valid employer contribution amount.";
      }
    }

    if (activeTab === "bonus" && bonus.is_enabled) {
      if (!bonus.payment_frequency) errors.bonus_frequency = "Payment frequency is required.";
      const pct = Number(bonus.bonus_percent);
      if (!Number.isFinite(pct) || pct < 8.33 || pct > 20) {
        errors.bonus_percent = "Bonus % must be between 8.33 and 20.";
      }
    }

    return errors;
  }, [activeTab, reg, epf, esi, pt, lwf, bonus, pfState]);

  const setPfCode = (v) => {
    setEpf((p) => ({ ...p, establishment_code: v }));
    setReg((p) => ({ ...p, epfo_establishment_code: v }));
  };

  const changePfState = (next) => {
    const code = epf.establishment_code || reg.epfo_establishment_code || "";
    const prevLead = `${getPfStateConfig(pfState).prefix}/`;
    // Swap the state prefix, keeping office / establishment / extension.
    const nextCode = code.startsWith(prevLead)
      ? `${getPfStateConfig(next).prefix}/${code.slice(prevLead.length)}`
      : code;
    setPfState(next);
    if (nextCode !== code) setPfCode(nextCode);
    const live = pfLiveStatus(nextCode, next);
    setFieldErrors((err) => ({ ...err, epfo_establishment_code: live.error || undefined }));
    markDirty();
  };

  const saveCurrentStep = async () => {
    const epfoCodeRes = validateStatutoryFormat(
      "epfo_establishment_code",
      epf.establishment_code || reg.epfo_establishment_code,
      { required: false },
    );
    const esicRes = validateStatutoryFormat(
      "esic_registration_number",
      esi.registration_number || reg.esic_registration_number,
      { required: false },
    );
    const epfoCode = epfoCodeRes.value || "";
    const esicReg = esicRes.value || "";

    if (activeTab === "registrations") {
      const nextReg = { ...reg };
      [
        "company_pan",
        "company_tan",
        "epfo_establishment_code",
        "esic_registration_number",
        "pt_registration_number",
        "pt_enrollment_number",
        "gst_number",
        "cin_number",
      ].forEach((key) => {
        const r = validateStatutoryFormat(key, nextReg[key], { required: false });
        if (r.ok) nextReg[key] = r.value;
      });
      await saveRegistrations(nextReg);
      setReg(nextReg);
      if (nextReg.epfo_establishment_code) {
        setEpf((p) => ({ ...p, establishment_code: nextReg.epfo_establishment_code }));
      }
      if (nextReg.esic_registration_number) {
        setEsi((p) => ({ ...p, registration_number: nextReg.esic_registration_number }));
      }
      return;
    }

    if (activeTab === "epf") {
      const payload = {
        is_enabled: epf.is_enabled,
        establishment_code: epfoCode,
        employer_contribution_mode: epf.employer_contribution_mode,
        effective_from: epf.effective_from,
        options: {
          ...epf.options,
          include_employer_in_ctc: Boolean(epf.include_employer_pf_in_ctc),
          include_edli_in_ctc: Boolean(epf.include_employer_pf_charges_in_ctc),
          include_admin_in_ctc: Boolean(epf.include_employer_pf_charges_in_ctc),
          include_pf_charges_in_ctc: Boolean(epf.include_employer_pf_charges_in_ctc),
          pf_applies_to: epf.pf_applies_to?.length ? epf.pf_applies_to : ["all"],
        },
      };
      await saveEpf(payload);
      setEpf((p) => ({
        ...p,
        ...payload,
        include_employer_pf_in_ctc: payload.options.include_employer_in_ctc,
        include_employer_pf_charges_in_ctc: payload.options.include_pf_charges_in_ctc,
        pf_applies_to: payload.options.pf_applies_to,
      }));
      setReg((p) => ({ ...p, epfo_establishment_code: epfoCode }));
      await saveRegistrations({ ...reg, epfo_establishment_code: epfoCode, esic_registration_number: esicReg || reg.esic_registration_number });
      return;
    }

    if (activeTab === "esi") {
      const payload = { ...esi, registration_number: esicReg };
      await saveEsi(payload);
      setEsi(payload);
      setReg((p) => ({ ...p, esic_registration_number: esicReg }));
      await saveRegistrations({ ...reg, esic_registration_number: esicReg, epfo_establishment_code: epfoCode || reg.epfo_establishment_code });
      return;
    }

    if (activeTab === "pt") {
      const ptReg = validateStatutoryFormat(
        "pt_registration_number",
        pt.registration_number || reg.pt_registration_number,
        { required: false },
      );
      const payload = { ...pt, registration_number: ptReg.value || pt.registration_number };
      const savedRow = await savePt(payload);
      setPt(payload);
      setPtSaved(savedRow || payload);
      return;
    }

    if (activeTab === "lwf") {
      await saveLwf(lwf);
      return;
    }

    if (activeTab === "bonus") {
      await saveBonus(bonus);
      return;
    }

    if (activeTab === "dashboard") {
      const dash = await fetchStatutoryDashboard();
      setDashboard(dash);
    }
  };

  const finishContinueAfterSave = async () => {
    setDirty(false);
    setCompletedSteps((prev) => new Set([...prev, activeTab]));
    const nextIdx = stepIndex + 1;
    if (hasNextSetupStep) {
      toast.success(`${STATUTORY_SETUP_STEPS[stepIndex].label} saved`);
      selectTab(STATUTORY_SETUP_STEPS[nextIdx].id, { force: true });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      try {
        sessionStorage.setItem(STATUTORY_FINISH_KEY, "1");
      } catch {
        /* ignore */
      }
      await markStatutorySetupConfigured();
      await refetchSetupProgress?.();
      setFinished(true);
      setSuccess("Statutory setup is complete. Your organisation is ready for compliance payroll.");
      toast.success("Statutory setup complete!", { title: "All steps done" });
      navigateToSettingsHome(navigate, { replace: true });
    }
  };

  // Step bar that calls this is commented out; kept with the rest of the wizard.
  // eslint-disable-next-line no-unused-vars
  const handleContinue = async () => {
    const errors = validateCurrentStep();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError("Please fix the highlighted fields before continuing.");
      focusFirstError(errors);
      toast.error("Fix the highlighted fields to continue.");
      return;
    }

    // Mid-month PF changes must choose when the new rule set becomes active.
    if (activeTab === "epf" && epf.is_enabled && epf.effective_from && !isFirstOfMonth(epf.effective_from)) {
      setEpfEffectiveModal({
        selectedDate: epf.effective_from,
        nextMonthFirst: nextMonthFirstISO(epf.effective_from),
      });
      return;
    }

    try {
      setSaving(true);
      setError("");
      await saveCurrentStep();
      await finishContinueAfterSave();
    } catch (e) {
      setError(formatStatutoryError(e, "Could not save this step"));
      toast.error(formatStatutoryError(e, "Could not save this step"));
    } finally {
      setSaving(false);
    }
  };

  const confirmEpfEffectiveChoice = async (mode) => {
    if (!epfEffectiveModal) return;
    const chosen =
      mode === "next_month" ? epfEffectiveModal.nextMonthFirst : epfEffectiveModal.selectedDate;
    setEpf((p) => ({ ...p, effective_from: chosen }));
    setEpfEffectiveModal(null);
    try {
      setSaving(true);
      setError("");
      // Persist with the chosen effective date (state update is async — pass via temporary patch).
      const epfoCodeRes = validateStatutoryFormat(
        "epfo_establishment_code",
        epf.establishment_code || reg.epfo_establishment_code,
        { required: false },
      );
      const epfoCode = epfoCodeRes.value || "";
      const esicRes = validateStatutoryFormat(
        "esic_registration_number",
        esi.registration_number || reg.esic_registration_number,
        { required: false },
      );
      const esicReg = esicRes.value || "";
      const payload = {
        is_enabled: epf.is_enabled,
        establishment_code: epfoCode,
        employer_contribution_mode: epf.employer_contribution_mode,
        effective_from: chosen,
        options: {
          ...epf.options,
          include_employer_in_ctc: Boolean(epf.include_employer_pf_in_ctc),
          include_edli_in_ctc: Boolean(epf.include_employer_pf_charges_in_ctc),
          include_admin_in_ctc: Boolean(epf.include_employer_pf_charges_in_ctc),
          include_pf_charges_in_ctc: Boolean(epf.include_employer_pf_charges_in_ctc),
          pf_applies_to: epf.pf_applies_to?.length ? epf.pf_applies_to : ["all"],
        },
      };
      await saveEpf(payload);
      setEpf((p) => ({
        ...p,
        ...payload,
        include_employer_pf_in_ctc: payload.options.include_employer_in_ctc,
        include_employer_pf_charges_in_ctc: payload.options.include_pf_charges_in_ctc,
        pf_applies_to: payload.options.pf_applies_to,
      }));
      setReg((p) => ({ ...p, epfo_establishment_code: epfoCode }));
      await saveRegistrations({
        ...reg,
        epfo_establishment_code: epfoCode,
        esic_registration_number: esicReg || reg.esic_registration_number,
      });
      await finishContinueAfterSave();
    } catch (e) {
      setError(formatStatutoryError(e, "Could not save this step"));
      toast.error(formatStatutoryError(e, "Could not save this step"));
    } finally {
      setSaving(false);
    }
  };

  // ── Per-tab Save / Reset (replaces the Back / Next step bar) ──
  // Snapshot of the last loaded / saved values, captured after the state settles.
  const savedSnapshot = useRef(null);
  const captureSnapshot = useRef(true); // first capture happens once the initial load finishes
  useEffect(() => {
    if (loading || saving || !captureSnapshot.current) return;
    captureSnapshot.current = false;
    savedSnapshot.current = { reg, epf, esi, lwf, bonus, pfState };
  }, [loading, saving, reg, epf, esi, lwf, bonus, pfState]);

  const handleSaveTab = async () => {
    const errors = validateCurrentStep();
    setFieldErrors(errors);
    if (Object.keys(errors).some((k) => errors[k])) {
      setError("Please fix the highlighted fields before saving.");
      focusFirstError(errors);
      toast.error("Fix the highlighted fields before saving.");
      return;
    }
    try {
      setSaving(true);
      setError("");
      await saveCurrentStep();
      setDirty(false);
      setCompletedSteps((prev) => new Set([...prev, activeTab]));
      captureSnapshot.current = true;
      toast.success(`${STATUTORY_SETUP_STEPS[stepIndex]?.label || "Settings"} saved`);
      navigateToSettingsHome(navigate, { replace: true });
    } catch (e) {
      setError(formatStatutoryError(e, "Could not save these settings"));
      toast.error(formatStatutoryError(e, "Could not save these settings"));
    } finally {
      setSaving(false);
    }
  };

  const handleResetTab = () => {
    const snap = savedSnapshot.current;
    if (!snap) return;
    setReg(snap.reg);
    setEpf(snap.epf);
    setEsi(snap.esi);
    setLwf(snap.lwf);
    setBonus(snap.bonus);
    setPfState(snap.pfState);
    setFieldErrors({});
    setError("");
    setDirty(false);
  };

  const tabActions = (
    <div className="stat-panel-actions">
      <button type="button" className="stat-btn stat-btn-ghost" onClick={handleResetTab} disabled={saving || !dirty}>
        Reset
      </button>
      <button type="button" className="stat-btn stat-btn-primary" onClick={handleSaveTab} disabled={saving}>
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );

  // eslint-disable-next-line no-unused-vars
  const handleBackStep = () => {
    if (stepIndex > 0) {
      selectTab(STATUTORY_SETUP_STEPS[stepIndex - 1].id);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // eslint-disable-next-line no-unused-vars
  const headerCards = dashboard?.header_cards || [];

  const handleFileUpload = (docType, file) => {
    if (!file) return;
    const entry = {
      doc_type: docType,
      file_name: file.name,
      uploaded_by: "current_user",
      uploaded_date: new Date().toISOString(),
      document_version: 1,
    };
    setReg((p) => ({ ...p, attachments: [...(p.attachments || []), entry] }));
    markDirty();
  };

  if (loading) {
    return (
      <div className="stat-page">
        <div className="stat-loading">Loading statutory compliance module…</div>
      </div>
    );
  }

  return (
    <div className="stat-page">
      <div className="stat-page-inner">
        {activeTab !== "pt" && (
        <div className="sw-page-hero">
          <div className="sw-page-hero-icon"><Shield size={22} /></div>
          <div className="sw-page-hero-text">
            <h1 className="sw-page-hero-title">Statutory Components</h1>
            <p className="sw-page-hero-sub">Configure your organisation's statutory compliance settings. Select a module to manage its rules and parameters.</p>
          </div>
        </div>
        )}

        {finished ? (
          <div className="stat-finish">
            <div className="stat-finish-badge">✓</div>
            <h2>Statutory setup complete</h2>
            <p>EPF, ESI, PT, LWF and Bonus are configured. Taking you to All Settings…</p>
            <button type="button" className="stat-btn stat-btn-primary" onClick={() => navigateToSettingsHome(navigate, { replace: true })}>
              Back to All Settings
            </button>
          </div>
        ) : null}

        {error && <div className="stat-alert error" data-auto-scroll-alert="true" role="alert">{String(error)}</div>}
        <SuccessCelebration
          open={Boolean(success)}
          title={finished ? "Setup complete" : "Updated successfully"}
          message={success}
          hint={finished ? "Returning to All Settings — your statutory compliance is ready." : "Statutory compliance settings are now active for payroll."}
          onDismiss={() => setSuccess("")}
          autoDismissMs={finished ? 2500 : 8000}
        />

        {/* {!finished && (
        <nav className="stat-stepper" aria-label="Statutory setup progress">
          {STATUTORY_SETUP_STEPS.map((s, i) => {
            const done = completedSteps.has(s.id);
            const current = activeTab === s.id;
            const unlocked = done || current || i === 0 || completedSteps.has(STATUTORY_SETUP_STEPS[i - 1]?.id);
            return (
              <button
                key={s.id}
                type="button"
                className={`stat-step${done ? " done" : ""}${current ? " current" : ""}${!unlocked ? " locked" : ""}`}
                disabled={!unlocked}
                onClick={() => unlocked && selectTab(s.id)}
              >
                <span className="stat-step-dot">{done ? "✓" : i + 1}</span>
                <span className="stat-step-label">
                  <span className="stat-step-num">Step {i + 1}</span>
                  {s.short}
                </span>
              </button>
            );
          })}
        </nav>
        )} */}

        {/* {!finished && (
        <div className="stat-header-cards">
          {(headerCards.length ? headerCards : TABS.slice(0, 5).map((t) => ({ component: t.label, enabled: false, compliance_health_pct: 0 }))).map(
            (c) => {
              const tabId = HEADER_CARD_TAB[c.component];
              const missing = Array.isArray(c.missing_fields) ? c.missing_fields : [];
              const openCard = () => {
                if (!tabId) return;
                selectTab(tabId, { force: true });
                if (!missing.length) {
                  setFieldErrors({});
                  return;
                }
                const errors = {};
                missing.forEach((m) => {
                  if (m?.key) errors[m.key] = m.message || `${m.label || m.key} is required.`;
                });
                setFieldErrors(errors);
                setTimeout(() => focusFirstError(errors), 120);
                toast.error(`${c.component}: fill ${missing.map((m) => m.label).filter(Boolean).join(", ")}`);
              };
              return (
              <div
                key={c.component}
                className={`stat-hcard${tabId ? " clickable" : ""}${missing.length ? " needs-attention" : ""}`}
                role={tabId ? "button" : undefined}
                tabIndex={tabId ? 0 : undefined}
                onClick={openCard}
                onKeyDown={(e) => {
                  if (!tabId) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    openCard();
                  }
                }}
              >
                <div className="stat-hcard-title">{c.component}</div>
                <div className={`stat-hcard-status ${c.enabled ? "on" : "off"}`}>
                  <span>{c.enabled ? "● Enabled" : "○ Disabled"}</span>
                </div>
                <div className="stat-health-bar">
                  <div className="stat-health-fill" style={{ width: `${c.compliance_health_pct || 0}%` }} />
                </div>
                <div className="stat-hcard-meta">
                  {c.registration_number && <div>Reg: {c.registration_number}</div>}
                  <div>Covered: {c.employees_covered ?? 0}</div>
                  {c.next_filing_date && <div>Next filing: {c.next_filing_date}</div>}
                  <div>Health: {c.compliance_health_pct ?? 0}%</div>
                </div>
                {missing.length > 0 && (
                  <ul className="stat-hcard-missing">
                    {missing.map((m) => (
                      <li key={m.key}>{m.label}</li>
                    ))}
                  </ul>
                )}
              </div>
              );
            }
          )}
        </div>
        )} */}

        {/* {!finished && isSetupStep ? (
          <p className="stat-step-kicker">
            Step {stepIndex + 1} of {STATUTORY_SETUP_STEPS.length} — {STATUTORY_SETUP_STEPS[stepIndex]?.label}
          </p>
        ) : null} */}

        {!finished && (
        <>
        {activeTab === "registrations" && (
          <div className="stat-panel">
            <h2>Government Registrations</h2>
            <p className="sub">Company-level registration numbers and compliance certificates.</p>
            <div className="stat-reg-grid">
              {[
                ["company_pan", "Company PAN", true],
                ["company_tan", "Company TAN", true],
                ["epfo_establishment_code", "EPFO Establishment Code", true],
                ["esic_registration_number", "ESIC Registration Number", true],
                ["pt_registration_number", "PT Registration Number", true],
                ["pt_enrollment_number", "PT Enrollment Number", true],
                ["lwf_number", "Labour Welfare Fund Number", false],
                ["gst_number", "GST Number", true],
                ["cin_number", "CIN Number", true],
                ["shop_establishment_number", "Shop & Establishment Number", false],
              ].map(([key, label, hasFormat]) => (
                <Field
                  key={key}
                  label={label}
                  fieldKey={key}
                  error={fieldErrors[key]}
                  hint={hasFormat ? formatHint(key) : null}
                >
                  <input
                    className={fieldErrors[key] ? "stat-input-err" : ""}
                    value={reg[key] || ""}
                    placeholder={hasFormat ? `e.g. ${formatExample(key)}` : undefined}
                    onChange={(e) => {
                      let v = e.target.value;
                      // Keep ESI digits as-is; uppercase letter-based IDs only.
                      if (hasFormat && key !== "esic_registration_number") v = v.toUpperCase();
                      setReg((p) => ({ ...p, [key]: v }));
                      if (key === "epfo_establishment_code") {
                        setEpf((p) => ({ ...p, establishment_code: v }));
                      }
                      if (key === "esic_registration_number") {
                        setEsi((p) => ({ ...p, registration_number: v }));
                      }
                      clearFieldError(key);
                      markDirty();
                    }}
                    onBlur={(e) => {
                      if (!hasFormat) return;
                      const res = validateStatutoryFormat(key, e.target.value, { required: false });
                      if (!res.ok) setFieldErrors((err) => ({ ...err, [key]: res.error }));
                      else if (res.value !== e.target.value) setReg((p) => ({ ...p, [key]: res.value }));
                    }}
                  />
                </Field>
              ))}
            </div>
            <h3 style={{ marginTop: "1.25rem", fontSize: "0.9rem", fontWeight: 600 }}>Certificates</h3>
            <div className="stat-grid-2" style={{ marginTop: "0.75rem" }}>
              {["EPFO", "ESIC", "PT", "LWF", "GST"].map((doc) => (
                <div key={doc}>
                  <div className="stat-upload-zone">
                    <input
                      type="file"
                      accept=".pdf,.png,.jpg,.jpeg"
                      style={{ display: "none" }}
                      id={`upload-${doc}`}
                      onChange={(e) => handleFileUpload(doc, e.target.files?.[0])}
                    />
                    <label htmlFor={`upload-${doc}`} style={{ cursor: "pointer" }}>
                      Upload {doc} Certificate
                      <br />
                      <span style={{ fontSize: "0.7rem" }}>PDF, PNG, JPG</span>
                    </label>
                  </div>
                </div>
              ))}
            </div>
            {(reg.attachments || []).length > 0 && (
              <ul style={{ marginTop: "0.75rem", fontSize: "0.8rem", color: "#475569" }}>
                {reg.attachments.map((a, i) => (
                  <li key={i}>
                    {a.doc_type}: {a.file_name} (v{a.document_version}, {a.uploaded_date?.slice(0, 10)})
                  </li>
                ))}
              </ul>
            )}
            {tabActions}
          </div>
        )}

        {activeTab === "epf" && (
          <>
            {/* ── PF Sub-tabs: Basic / Advanced ── */}
            <div style={{ display: "flex", gap: 0, marginBottom: "1rem", borderBottom: "2px solid #e2e8f0" }}>
              <button
                type="button"
                role="tab"
                aria-selected={pfSubTab === "basic"}
                onClick={() => setPfSubTab("basic")}
                style={{
                  padding: "10px 24px",
                  fontSize: 13,
                  fontWeight: pfSubTab === "basic" ? 700 : 500,
                  color: pfSubTab === "basic" ? "#1e40af" : "#64748b",
                  background: pfSubTab === "basic" ? "#eff6ff" : "transparent",
                  border: "none",
                  borderBottom: pfSubTab === "basic" ? "2px solid #1e40af" : "2px solid transparent",
                  marginBottom: -2,
                  cursor: "pointer",
                  borderRadius: "6px 6px 0 0",
                  transition: "all 0.15s",
                }}
              >
                1. Basic Settings
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={pfSubTab === "advanced"}
                onClick={() => setPfSubTab("advanced")}
                style={{
                  padding: "10px 24px",
                  fontSize: 13,
                  fontWeight: pfSubTab === "advanced" ? 700 : 500,
                  color: pfSubTab === "advanced" ? "#1e40af" : "#64748b",
                  background: pfSubTab === "advanced" ? "#eff6ff" : "transparent",
                  border: "none",
                  borderBottom: pfSubTab === "advanced" ? "2px solid #1e40af" : "2px solid transparent",
                  marginBottom: -2,
                  cursor: "pointer",
                  borderRadius: "6px 6px 0 0",
                  transition: "all 0.15s",
                }}
              >
                2. Advanced Settings
              </button>
            </div>

            {pfSubTab === "advanced" ? (
              <PfAdvancedSettings />
            ) : (
            <>
            <div className="stat-grid-2">
              <div className="stat-panel">
                <h2>EPF Configuration</h2>
                <p className="sub">
                  Configure employee provident fund settings for your organisation.
                </p>
                <Toggle checked={epf.is_enabled} onChange={(v) => { setEpf((p) => ({ ...p, is_enabled: v })); markDirty(); }} label="Enable EPF" />
                {epf.is_enabled && (
                  <>
                    {/* ── Applies To ── */}
                    <div style={{ marginTop: "1rem", marginBottom: "1rem", padding: "1rem", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 8 }}>
                        Applies To <span style={{ color: "#ef4444" }}>*</span>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {PF_EMPLOYMENT_TYPES.map(({ key, label }) => (
                          <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={(epf.pf_applies_to || []).includes(key)}
                              onChange={(e) => {
                                setEpf((p) => ({
                                  ...p,
                                  pf_applies_to: e.target.checked
                                    ? [...(p.pf_applies_to || []), key]
                                    : (p.pf_applies_to || []).filter((t) => t !== key),
                                }));
                                markDirty();
                              }}
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* ── State ── */}
                    <Field label="State" required fieldKey="pf_state">
                      <select value={pfState} onChange={(e) => changePfState(e.target.value)}>
                        {PF_STATE_OPTIONS.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </Field>

                    {/* ── Registration ── */}
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 4, marginTop: "0.5rem" }}>Registration</div>
                    <Field
                      label="PF Establishment / Registration No."
                      required
                      fieldKey="epfo_establishment_code"
                      error={fieldErrors.epfo_establishment_code}
                      hint={(() => {
                        // Soft progress guidance while the number is still being built (hidden while an error shows).
                        const { hint } = pfLiveStatus(epf.establishment_code || reg.epfo_establishment_code, pfState);
                        const fmt = getPfStateConfig(pfState).format;
                        return hint && !fieldErrors.epfo_establishment_code ? `${fmt} — ${hint}` : fmt;
                      })()}
                    >
                      <PfRegistrationInput
                        state={pfState}
                        className={fieldErrors.epfo_establishment_code ? "stat-input-err" : ""}
                        value={epf.establishment_code || reg.epfo_establishment_code || ""}
                        onChange={(v) => {
                          setPfCode(v);
                          const live = pfLiveStatus(v, pfState);
                          setFieldErrors((err) => ({ ...err, epfo_establishment_code: live.error || undefined }));
                          markDirty();
                        }}
                        onInvalid={(msg) => setFieldErrors((err) => ({ ...err, epfo_establishment_code: msg }))}
                        onBlur={(v) => {
                          // Re-check the whole value: also clears a stale error from a refused keystroke.
                          const res = validatePfRegistration(v, pfState, { required: true });
                          setFieldErrors((err) => ({ ...err, epfo_establishment_code: res.ok ? undefined : res.error }));
                        }}
                      />
                    </Field>
                    <Field
                      label="Effective From"
                      required
                      fieldKey="effective_from"
                      error={fieldErrors.effective_from}
                    >
                      <DatePickerField hideLabel value={epf.effective_from} onChange={(date) => { setEpf((p) => ({ ...p, effective_from: date })); clearFieldError("effective_from"); markDirty(); }} placeholder="Effective from" />
                    </Field>

                    {/* ── Contribution ── */}
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a", marginBottom: 4, marginTop: "1rem" }}>Contribution</div>
                    <Field
                      label="Employer Contribution Mode"
                      required
                      fieldKey="employer_contribution_mode"
                      error={fieldErrors.employer_contribution_mode}
                    >
                      <select
                        className={fieldErrors.employer_contribution_mode ? "stat-input-err" : ""}
                        value={epf.employer_contribution_mode}
                        onChange={(e) => {
                          setEpf((p) => ({ ...p, employer_contribution_mode: e.target.value }));
                          clearFieldError("employer_contribution_mode");
                          markDirty();
                        }}
                      >
                        <option value="actual_wage">12% Actual PF Wage</option>
                        {/* "capped_15000" is the stored key for capped mode; the cap is the statutory ceiling. */}
                        <option value="capped_15000">
                          Restrict PF Wage To {pfCeilingLabel} (PF amount = ₹{Math.round(pfWageCeiling * 0.12).toLocaleString("en-IN")})
                        </option>
                      </select>
                    </Field>

                    {/* ── Gross Treatment (employer PF inclusion in employee package) ── */}
                    <div style={{ marginTop: "1rem", marginBottom: "1rem", padding: "1rem", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 12 }}>Gross Treatment</div>
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
                          Include Employer PF in Employee Gross? <span style={{ color: "#ef4444" }}>*</span>
                        </div>
                        <div style={{ display: "flex", gap: 16 }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#334155", cursor: "pointer" }}>
                            <input type="radio" name="include_employer_pf_in_ctc" checked={!epf.include_employer_pf_in_ctc} onChange={() => { setEpf((p) => ({ ...p, include_employer_pf_in_ctc: false })); markDirty(); }} />
                            No
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#334155", cursor: "pointer" }}>
                            <input type="radio" name="include_employer_pf_in_ctc" checked={epf.include_employer_pf_in_ctc} onChange={() => { setEpf((p) => ({ ...p, include_employer_pf_in_ctc: true })); markDirty(); }} />
                            Yes
                          </label>
                        </div>
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
                          Include Employer PF-related Charges in Gross? <span style={{ color: "#ef4444" }}>*</span>
                        </div>
                        <div style={{ display: "flex", gap: 16 }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#334155", cursor: "pointer" }}>
                            <input type="radio" name="include_employer_pf_charges_in_ctc" checked={!epf.include_employer_pf_charges_in_ctc} onChange={() => { setEpf((p) => ({ ...p, include_employer_pf_charges_in_ctc: false })); markDirty(); }} />
                            No
                          </label>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#334155", cursor: "pointer" }}>
                            <input type="radio" name="include_employer_pf_charges_in_ctc" checked={epf.include_employer_pf_charges_in_ctc} onChange={() => { setEpf((p) => ({ ...p, include_employer_pf_charges_in_ctc: true })); markDirty(); }} />
                            Yes
                          </label>
                        </div>
                      </div>
                    </div>

                  </>
                )}
                {tabActions}
              </div>
              <div className="stat-calc-box">
                <LiveCalcHeader title="Real-Time PF Calculator" />
                <p className="sub" style={{ marginTop: 0, marginBottom: "0.75rem" }}>
                  Mode: {epf.employer_contribution_mode === "actual_wage" ? "12% on actual PF wage" : `PF wage capped at ${pfCeilingLabel}`}
                </p>
                <Field label="Basic Pay">
                  <input
                    type="number"
                    min="0"
                    value={pfCalc.basicPay}
                    onChange={(e) => setPfCalc((p) => ({ ...p, basicPay: e.target.value }))}
                  />
                </Field>
                <Field label="DA">
                  <input
                    type="number"
                    min="0"
                    value={pfCalc.da}
                    onChange={(e) => setPfCalc((p) => ({ ...p, da: e.target.value }))}
                  />
                </Field>
                {pfResult.warnings?.map((w, i) => (
                  <div key={i} className="stat-alert warn">{w}</div>
                ))}
                <CalcRow label="PF Wage" value={fmtINR(pfResult.pf_wage)} />
                <CalcRow label="Employee PF (12%)" value={fmtINR(pfResult.employee_pf)} />
                <CalcRow label="Employer PF (12%)" value={fmtINR(pfResult.employer_pf)} />
                <CalcRow label="EPS (8.33%)" value={fmtINR(pfResult.eps)} />
                <CalcRow label="EPF Share (3.67%)" value={fmtINR(pfResult.epf_share)} />
                <CalcRow label="Admin Charges (1.01%)" value={fmtINR(pfResult.edli + pfResult.admin_charges + pfResult.inspection_charges)} />
                <CalcRow label="Total Employer Cost" value={fmtINR(pfResult.total_employer_cost)} total />
              </div>
            </div>
            </>
            )}
          </>
        )}

        {activeTab === "esi" && (
          <div className="stat-grid-2">
            <div className="stat-panel">
              <h2>ESI Configuration</h2>
              <p className="sub">
                Configure employee state insurance settings for your organisation.
              </p>
              <Toggle checked={esi.is_enabled} onChange={(v) => { setEsi((p) => ({ ...p, is_enabled: v })); markDirty(); }} label="Enable ESI" />
              {esi.is_enabled && (
                <>
                  {/* ── Applies To ── */}
                  <div style={{ marginTop: "1rem", marginBottom: "1rem", padding: "1rem", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 8 }}>
                      Applies To <span style={{ color: "#ef4444" }}>*</span>
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {ESI_EMPLOYMENT_TYPES.map(({ key, label }) => (
                        <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", cursor: "pointer" }}>
                          <input
                            type="checkbox"
                            checked={(esi.esi_applies_to || []).includes(key)}
                            onChange={(e) => {
                              setEsi((p) => ({
                                ...p,
                                esi_applies_to: e.target.checked
                                  ? [...(p.esi_applies_to || []), key]
                                  : (p.esi_applies_to || []).filter((t) => t !== key),
                              }));
                              markDirty();
                            }}
                          />
                          {label}
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* ── Registration ── */}
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a", marginBottom: 4, marginTop: "0.5rem" }}>Registration</div>
                  <Field
                    label="ESI Employer / Establishment Registration No."
                    required
                    fieldKey="esic_registration_number"
                    error={fieldErrors.esic_registration_number}
                    hint={formatHint("esic_registration_number")}
                  >
                    <input
                      className={fieldErrors.esic_registration_number ? "stat-input-err" : ""}
                      value={esi.registration_number || reg.esic_registration_number || ""}
                      placeholder={formatExample("esic_registration_number")}
                      inputMode="numeric"
                      autoComplete="off"
                      onChange={(e) => {
                        // Digits only, max 17 — invalid characters never reach the field.
                        const el = e.target;
                        const raw = el.value;
                        const caret = el.selectionStart ?? raw.length;
                        const prev = esi.registration_number || reg.esic_registration_number || "";
                        const digits = raw.replace(/\D/g, "");
                        const pasted = e.nativeEvent?.inputType === "insertFromPaste";
                        // A typed 18th digit is refused outright; a long paste is trimmed to 17.
                        const overflow = !pasted && digits.length > 17;
                        const v = overflow ? prev : digits.slice(0, 17);
                        const pos = overflow
                          ? Math.max(0, caret - (raw.length - prev.length))
                          : Math.min(raw.slice(0, caret).replace(/\D/g, "").length, v.length);
                        const rejected = /\D/.test(raw);
                        setEsi((p) => ({ ...p, registration_number: v }));
                        setReg((p) => ({ ...p, esic_registration_number: v }));
                        if (pasted && v.length < 17) {
                          setFieldErrors((err) => ({ ...err, esic_registration_number: "Please enter exactly 17 digits. Format: 17 digits only." }));
                        } else if (rejected && !pasted) {
                          setFieldErrors((err) => ({ ...err, esic_registration_number: "Only numbers are allowed. Format: 17 digits only." }));
                        } else {
                          clearFieldError("esic_registration_number");
                        }
                        // Keep the caret in place when a stripped character leaves the value unchanged.
                        queueMicrotask(() => {
                          if (document.activeElement === el) el.setSelectionRange(pos, pos);
                        });
                        markDirty();
                      }}
                      onBlur={(e) => {
                        const res = validateStatutoryFormat("esic_registration_number", e.target.value, { required: true });
                        if (!res.ok) setFieldErrors((err) => ({ ...err, esic_registration_number: res.error }));
                        else {
                          setEsi((p) => ({ ...p, registration_number: res.value }));
                          setReg((p) => ({ ...p, esic_registration_number: res.value }));
                        }
                      }}
                    />
                  </Field>
                  <Field
                    label="Coverage State"
                    required
                    fieldKey="coverage_state"
                    error={fieldErrors.coverage_state}
                  >
                    <select
                      className={fieldErrors.coverage_state ? "stat-input-err" : ""}
                      value={esi.coverage_state}
                      onChange={(e) => {
                        setEsi((p) => ({ ...p, coverage_state: e.target.value }));
                        clearFieldError("coverage_state");
                        markDirty();
                      }}
                    >
                      <option value="">Select state</option>
                      {states.map((s) => (
                        <option key={s.code} value={s.code}>{s.name}</option>
                      ))}
                    </select>
                  </Field>
                  <Field
                    label="Effective From"
                    required
                    fieldKey="esi_effective_from"
                    error={fieldErrors.esi_effective_from}
                  >
                    <DatePickerField hideLabel value={esi.effective_from} onChange={(date) => { setEsi((p) => ({ ...p, effective_from: date })); clearFieldError("esi_effective_from"); markDirty(); }} placeholder="Effective from" />
                  </Field>

                  {/* ── Statutory Policy ── */}
                  <div style={{ fontWeight: 700, fontSize: 14, color: "#0f172a", marginBottom: 4, marginTop: "1rem" }}>Statutory Policy</div>
                  <Field label="Employee Contribution" fieldKey="employee_contribution_rate">
                    <select
                      value={esi.employee_contribution_rate}
                      onChange={(e) => { setEsi((p) => ({ ...p, employee_contribution_rate: e.target.value })); markDirty(); }}
                    >
                      <option value="0.75">Standard Statutory — 0.75%</option>
                    </select>
                  </Field>
                  <Field label="Employer Contribution" fieldKey="employer_contribution_rate">
                    <select
                      value={esi.employer_contribution_rate}
                      onChange={(e) => { setEsi((p) => ({ ...p, employer_contribution_rate: e.target.value })); markDirty(); }}
                    >
                      <option value="3.25">Standard Statutory — 3.25%</option>
                    </select>
                  </Field>
                  <Field
                    label="ESI Wage Basis"
                    required
                    fieldKey="esi_wage_basis"
                    error={fieldErrors.esi_wage_basis}
                  >
                    <select
                      className={fieldErrors.esi_wage_basis ? "stat-input-err" : ""}
                      value={esi.esi_wage_basis}
                      onChange={(e) => {
                        setEsi((p) => ({ ...p, esi_wage_basis: e.target.value }));
                        clearFieldError("esi_wage_basis");
                        markDirty();
                      }}
                    >
                      <option value="">Select wage basis</option>
                      <option value="gross">Gross Salary</option>
                      <option value="basic">Basic Salary</option>
                    </select>
                  </Field>

                  {/* ── Gross Treatment ── */}
                  <div style={{ marginTop: "1rem", marginBottom: "1rem", padding: "1rem", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0" }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: "#1e293b", marginBottom: 12 }}>Gross Treatment</div>
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
                        Include Employer ESI in Employee Gross? <span style={{ color: "#ef4444" }}>*</span>
                      </div>
                      <div style={{ display: "flex", gap: 16 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#334155", cursor: "pointer" }}>
                          <input type="radio" name="include_employer_esi_in_ctc" checked={!esi.include_employer_esi_in_ctc} onChange={() => { setEsi((p) => ({ ...p, include_employer_esi_in_ctc: false })); markDirty(); }} />
                          No
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#334155", cursor: "pointer" }}>
                          <input type="radio" name="include_employer_esi_in_ctc" checked={esi.include_employer_esi_in_ctc} onChange={() => { setEsi((p) => ({ ...p, include_employer_esi_in_ctc: true })); markDirty(); }} />
                          Yes
                        </label>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#334155", marginBottom: 6 }}>
                        Include Employer ESI-related Charges in Gross? <span style={{ color: "#ef4444" }}>*</span>
                      </div>
                      <div style={{ display: "flex", gap: 16 }}>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#334155", cursor: "pointer" }}>
                          <input type="radio" name="include_employer_esi_charges_in_ctc" checked={!esi.include_employer_esi_charges_in_ctc} onChange={() => { setEsi((p) => ({ ...p, include_employer_esi_charges_in_ctc: false })); markDirty(); }} />
                          No
                        </label>
                        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#334155", cursor: "pointer" }}>
                          <input type="radio" name="include_employer_esi_charges_in_ctc" checked={esi.include_employer_esi_charges_in_ctc} onChange={() => { setEsi((p) => ({ ...p, include_employer_esi_charges_in_ctc: true })); markDirty(); }} />
                          Yes
                        </label>
                      </div>
                    </div>
                  </div>

                </>
              )}
              {tabActions}
            </div>
            <div className="stat-calc-box">
              <LiveCalcHeader title="Real-Time ESI Calculator" />
              <p className="sub" style={{ marginTop: 0, marginBottom: "0.75rem" }}>
                Wage Basis: {esi.esi_wage_basis === "basic" ? "Basic Salary" : "Gross Salary"}
              </p>
              {esi.esi_wage_basis === "basic" ? (
                <Field label="Basic Salary">
                  <input type="number" min="0" value={esiCalc.basic} onChange={(e) => setEsiCalc((p) => ({ ...p, basic: e.target.value }))} />
                </Field>
              ) : (
                <Field label="Gross Salary">
                  <input type="number" min="0" value={esiCalc.gross} onChange={(e) => setEsiCalc((p) => ({ ...p, gross: e.target.value }))} />
                </Field>
              )}
              {!esiResult.eligible && <div className="stat-alert error">{esiResult.message}</div>}
              <CalcRow label="ESI Wages" value={fmtINR(esiResult.gross_pay)} />
              <CalcRow label="Employee (0.75%)" value={fmtINR(esiResult.employee_contribution)} />
              <CalcRow label="Employer (3.25%)" value={fmtINR(esiResult.employer_contribution)} />
              <CalcRow label="Total Cost" value={fmtINR(esiResult.total_cost)} total />
              <p style={{ fontSize: "0.75rem", color: "#64748b", marginTop: "0.5rem" }}>Eligibility: Gross ≤ ₹21,000</p>
            </div>
          </div>
        )}

        {activeTab === "pt" && (
          <ProfessionalTaxPanel
            savedPt={ptSaved}
            fallbackRegistrationNumber={reg.pt_registration_number}
            onSaved={(row) => {
              setPt((p) => ({ ...p, ...row }));
              setPtSaved(row);
            }}
          />
        )}

        {activeTab === "lwf" && (
          <div className="stat-grid-2">
            <div className="stat-panel">
              <h2>Labour Welfare Fund</h2>
              <Toggle checked={lwf.is_enabled} onChange={(v) => { setLwf((p) => ({ ...p, is_enabled: v })); markDirty(); }} label="Enable LWF" />
              <Field
                label="State"
                required={lwf.is_enabled}
                fieldKey="lwf_state_code"
                error={fieldErrors.lwf_state_code}
              >
                <select
                  className={fieldErrors.lwf_state_code ? "stat-input-err" : ""}
                  value={lwf.state_code}
                  onChange={(e) => {
                    setLwf((p) => ({ ...p, state_code: e.target.value }));
                    clearFieldError("lwf_state_code");
                    markDirty();
                  }}
                >
                  {states.map((s) => (
                    <option key={s.code} value={s.code}>{s.name}</option>
                  ))}
                </select>
              </Field>
              <Field
                label="Employee Contribution (₹)"
                required={lwf.is_enabled}
                fieldKey="lwf_employee"
                error={fieldErrors.lwf_employee}
              >
                <input
                  type="number"
                  className={fieldErrors.lwf_employee ? "stat-input-err" : ""}
                  value={lwf.employee_contribution}
                  onChange={(e) => {
                    setLwf((p) => ({ ...p, employee_contribution: e.target.value }));
                    clearFieldError("lwf_employee");
                    markDirty();
                  }}
                />
              </Field>
              <Field
                label="Employer Contribution (₹)"
                required={lwf.is_enabled}
                fieldKey="lwf_employer"
                error={fieldErrors.lwf_employer}
              >
                <input
                  type="number"
                  className={fieldErrors.lwf_employer ? "stat-input-err" : ""}
                  value={lwf.employer_contribution}
                  onChange={(e) => {
                    setLwf((p) => ({ ...p, employer_contribution: e.target.value }));
                    clearFieldError("lwf_employer");
                    markDirty();
                  }}
                />
              </Field>
              <Field label="Frequency">
                <select value={lwf.frequency} onChange={(e) => { setLwf((p) => ({ ...p, frequency: e.target.value })); markDirty(); }}>
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="half_yearly">Half-Yearly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </Field>
              {tabActions}
            </div>
            <div className="stat-calc-box">
              <LiveCalcHeader title="LWF Calculator" />
              <Field label="Salary (reference)">
                <input type="number" min="0" value={lwfCalc.salary} onChange={(e) => setLwfCalc({ salary: e.target.value })} />
              </Field>
              <CalcRow label="Employee LWF" value={fmtINR(lwf.employee_contribution)} />
              <CalcRow label="Employer LWF" value={fmtINR(lwf.employer_contribution)} total />
            </div>
          </div>
        )}

        {activeTab === "bonus" && (
          <div className="stat-grid-2">
            <div className="stat-panel">
              <h2>Statutory Bonus</h2>
              <Toggle checked={bonus.is_enabled} onChange={(v) => { setBonus((p) => ({ ...p, is_enabled: v })); markDirty(); }} label="Enable Bonus" />
              <Field
                label="Payment Frequency"
                required={bonus.is_enabled}
                fieldKey="bonus_frequency"
                error={fieldErrors.bonus_frequency}
              >
                <select
                  className={fieldErrors.bonus_frequency ? "stat-input-err" : ""}
                  value={bonus.payment_frequency}
                  onChange={(e) => {
                    setBonus((p) => ({ ...p, payment_frequency: e.target.value }));
                    clearFieldError("bonus_frequency");
                    markDirty();
                  }}
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </Field>
              <Field
                label={`Bonus % (${bonus.bonus_percent}%)`}
                required={bonus.is_enabled}
                fieldKey="bonus_percent"
                error={fieldErrors.bonus_percent}
              >
                <input
                  type="range"
                  min="8.33"
                  max="20"
                  step="0.01"
                  className={fieldErrors.bonus_percent ? "stat-input-err" : ""}
                  value={bonus.bonus_percent}
                  onChange={(e) => {
                    setBonus((p) => ({ ...p, bonus_percent: Number(e.target.value) }));
                    clearFieldError("bonus_percent");
                    markDirty();
                  }}
                />
              </Field>
              <p className="sub">Eligibility: Salary ≤ ₹21,000 and worked ≥ 30 days</p>
              {tabActions}
            </div>
            <div>
              <div className="stat-calc-box">
                <LiveCalcHeader title="Bonus Calculator" />
                <Field label="Basic + DA">
                  <input type="number" min="0" value={bonusCalc.basicDa} onChange={(e) => setBonusCalc((p) => ({ ...p, basicDa: e.target.value }))} />
                </Field>
                <Field label="Worked Days">
                  <input type="number" min="0" max="31" value={bonusCalc.workedDays} onChange={(e) => setBonusCalc((p) => ({ ...p, workedDays: e.target.value }))} />
                </Field>
                <p className="sub" style={{ marginTop: 0 }}>Bonus rate: {bonus.bonus_percent}%</p>
                <CalcRow label="Eligible" value={bonusResult.eligible ? "Yes" : "No"} />
                <CalcRow label="Monthly Bonus" value={fmtINR(bonusResult.monthly_bonus)} />
                <CalcRow label="Annual Bonus" value={fmtINR(bonusResult.annual_bonus)} total />
              </div>
              <div className="stat-panel" style={{ marginTop: "1rem" }}>
                <h3>Bonus Projection</h3>
                <CalcRow label="Projected Annual Liability" value={fmtINR(bonusResult.annual_bonus * (dashboard?.bonus_eligible || 1))} />
                <CalcRow label="Eligible Employees (est.)" value={dashboard?.bonus_eligible ?? "—"} />
              </div>
            </div>
          </div>
        )}

        {activeTab === "dashboard" && (
          <div className="stat-panel">
            <h2>Setup Review &amp; Completion</h2>
            <p className="sub">
              Review your statutory configuration below. Click <strong>Submit</strong> when everything looks correct.
            </p>
            <ul className="stat-finish-checklist">
              {STATUTORY_SETUP_STEPS.filter((s) => s.id !== "dashboard").map((s) => (
                <li
                  key={s.id}
                  className={`${completedSteps.has(s.id) ? "done" : ""} clickable`}
                  role="button"
                  tabIndex={0}
                  onClick={() => selectTab(s.id, { force: true })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      selectTab(s.id, { force: true });
                    }
                  }}
                >
                  {completedSteps.has(s.id) ? "✓" : "○"} {s.label}
                  {s.id === "epf" ? ` — ${epf.is_enabled ? "Enabled" : "Disabled"}` : ""}
                  {s.id === "esi" ? ` — ${esi.is_enabled ? "Enabled" : "Disabled"}` : ""}
                  {s.id === "pt" ? ` — ${pt.is_enabled ? "Enabled" : "Disabled"}` : ""}
                  {s.id === "lwf" ? ` — ${lwf.is_enabled ? "Enabled" : "Disabled"}` : ""}
                  {s.id === "bonus" ? ` — ${bonus.is_enabled ? "Enabled" : "Disabled"}` : ""}
                </li>
              ))}
            </ul>
            {dashboard ? (
              <>
                <h3 style={{ marginTop: "1.5rem", fontSize: "0.9rem" }}>Compliance Snapshot</h3>
                <div className="stat-dash-grid">
                  {[
                    ["Total Employees", dashboard.total_employees],
                    ["PF Employees", dashboard.pf_employees],
                    ["ESI Employees", dashboard.esi_employees],
                    ["PT Employees", dashboard.pt_employees],
                    ["LWF Employees", dashboard.lwf_employees],
                    ["Bonus Eligible", dashboard.bonus_eligible],
                  ].map(([lbl, val]) => (
                    <div key={lbl} className="stat-dash-metric">
                      <div className="val">{val}</div>
                      <div className="lbl">{lbl}</div>
                    </div>
                  ))}
                </div>
                <h3 style={{ marginTop: "1.5rem", fontSize: "0.9rem" }}>Contribution Summary (Current Month)</h3>
                <div className="stat-grid-2" style={{ maxWidth: 480 }}>
                  <CalcRow label="Employee" value={fmtINR(dashboard.employee_contribution_month)} />
                  <CalcRow label="Employer" value={fmtINR(dashboard.employer_contribution_month)} />
                  <CalcRow label="Grand Total" value={fmtINR(dashboard.grand_total_month)} />
                </div>
                <h3 style={{ marginTop: "1.5rem", fontSize: "0.9rem" }}>Compliance Calendar</h3>
                <div className="stat-table-wrap">
                  <table className="stat-table">
                    <thead>
                      <tr>
                        <th>Type</th>
                        <th>Title</th>
                        <th>Due</th>
                        <th>Days Left</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dashboard.calendar || []).map((c, i) => (
                        <tr key={i}>
                          <td><span className="stat-badge">{c.compliance_type}</span></td>
                          <td>{c.title}</td>
                          <td>{c.due_date}</td>
                          <td>{c.days_remaining}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {(dashboard.alerts || []).length > 0 && (
                  <>
                    <h3 style={{ marginTop: "1rem", fontSize: "0.9rem" }}>Alerts</h3>
                    {dashboard.alerts.map((a, i) => (
                      <div key={i} className={`stat-alert ${a.type === "warning" ? "warn" : "info"}`}>{a.message}</div>
                    ))}
                  </>
                )}
              </>
            ) : (
              <p className="sub">Loading compliance snapshot…</p>
            )}
          </div>
        )}

        {activeTab === "audit" && (
          <div className="stat-panel">
            <h2>Audit Logs</h2>
            <div className="stat-table-wrap">
              <table className="stat-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Entity</th>
                    <th>Field</th>
                    <th>Old</th>
                    <th>New</th>
                    <th>By</th>
                    <th>IP</th>
                  </tr>
                </thead>
                <tbody>
                  {auditLogs.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", color: "#94a3b8" }}>No audit entries yet</td>
                    </tr>
                  ) : (
                    auditLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{log.created_at?.slice(0, 19)?.replace("T", " ")}</td>
                        <td>{log.entity_type}</td>
                        <td>{log.field_name}</td>
                        <td>{log.old_value || "—"}</td>
                        <td>{log.new_value || "—"}</td>
                        <td>{log.changed_by}</td>
                        <td>{log.ip_address || "—"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        </>
        )}

        {/* Step bar replaced by per-tab Save / Reset (tabActions).
        {!finished && isSetupStep && !(activeTab === "epf" && pfSubTab === "advanced") ? (
          <div className="stat-save-bar">
            <div className={`stat-save-status ${saving ? "saving" : dirty ? "dirty" : "saved"}`}>
              {saving
                ? "Saving this step…"
                : draftSaving
                  ? "Autosaving draft…"
                  : dirty
                    ? "Unsaved changes on this step"
                    : `Step ${stepIndex + 1} of ${STATUTORY_SETUP_STEPS.length}`}
            </div>
            <div className="stat-save-actions">
              <button
                type="button"
                className="stat-btn stat-btn-ghost"
                onClick={handleBackStep}
                disabled={saving || stepIndex <= 0}
              >
                Back
              </button>
              <button
                type="button"
                className="stat-btn stat-btn-primary"
                onClick={handleContinue}
                disabled={saving || draftSaving}
              >
                {primaryContinueLabel}
              </button>
            </div>
          </div>
        ) : null} */}
      </div>

      {epfEffectiveModal ? (
        <div className="pt-modal-overlay" role="presentation" onClick={() => !saving && setEpfEffectiveModal(null)}>
          <div
            className="pt-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="epf-effective-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pt-modal-head">
              <h3 id="epf-effective-title">When should these PF changes apply?</h3>
            </div>
            <p style={{ margin: "0 0 1rem", fontSize: 13.5, color: "#475569", lineHeight: 1.5 }}>
              You selected <strong>{formatLongDate(epfEffectiveModal.selectedDate)}</strong>, which is after the
              1st of the month. Choose when the full PF configuration (rates, wage mode, Applies To, and Gross
              treatment) becomes active for payroll.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                type="button"
                className="stat-btn"
                style={{
                  textAlign: "left",
                  padding: "12px 14px",
                  border: "1px solid #c7d2fe",
                  background: "#eef2ff",
                  borderRadius: 10,
                  cursor: "pointer",
                }}
                disabled={saving}
                onClick={() => confirmEpfEffectiveChoice("selected")}
              >
                <div style={{ fontWeight: 700, fontSize: 13, color: "#312e81" }}>Apply from selected date</div>
                <div style={{ fontSize: 12, color: "#4338ca", marginTop: 4 }}>
                  Changes activate on {formatLongDate(epfEffectiveModal.selectedDate)} for the current month.
                </div>
              </button>
              <button
                type="button"
                className="stat-btn"
                style={{
                  textAlign: "left",
                  padding: "12px 14px",
                  border: "1px solid #bbf7d0",
                  background: "#ecfdf5",
                  borderRadius: 10,
                  cursor: "pointer",
                }}
                disabled={saving}
                onClick={() => confirmEpfEffectiveChoice("next_month")}
              >
                <div style={{ fontWeight: 700, fontSize: 13, color: "#065f46" }}>
                  Apply from next month 1st
                </div>
                <div style={{ fontSize: 12, color: "#047857", marginTop: 4 }}>
                  Recommended — changes activate on {formatLongDate(epfEffectiveModal.nextMonthFirst)}.
                </div>
              </button>
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <button
                type="button"
                className="stat-btn stat-btn-ghost"
                disabled={saving}
                onClick={() => setEpfEffectiveModal(null)}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
